import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { serviceCategories } from '../../../mocks/servicesData';

export default function ServiceDetails() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  return (
    <section id="service-details" className="py-28 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-amber-50 to-transparent rounded-full -translate-y-1/2 translate-x-1/3"></div>

      <div className="max-w-6xl mx-auto px-6 relative">
        <div
          ref={headerRef}
          className={`text-center mb-20 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400"></div>
            <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">
              What We Offer
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900 mb-5">
            Our Services in Detail
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto leading-relaxed">
            Every event is unique. Explore our core services and discover how we bring your vision to life with meticulous attention to detail.
          </p>
        </div>

        <div className="space-y-20">
          {serviceCategories.map((service, index) => {
            const isEven = index % 2 === 1;
            const isExpanded = expandedId === service.id;

            return (
              <div
                key={service.id}
                className={`flex flex-col ${isEven ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 items-center`}
              >
                <div className="lg:w-1/2 w-full">
                  <div className="relative rounded-2xl overflow-hidden group">
                    <div className="w-full h-80 lg:h-96">
                      <img
                        src={service.image}
                        alt={`${service.title} - House of FMR event planning service`}
                        title={`${service.title} service by House of FMR`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="absolute top-4 left-4">
                      <div className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                        <i className={`${service.icon} text-xl text-amber-600`}></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-1/2 w-full">
                  <span className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase">
                    {service.tagline}
                  </span>
                  <h3 className="text-3xl font-display text-stone-900 mt-2 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-stone-500 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[500px] opacity-100 mb-6' : 'max-h-32 opacity-100 mb-6'}`}>
                    {service.features.slice(0, isExpanded ? service.features.length : 4).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-xs text-amber-600"></i>
                        </div>
                        <span className="text-sm text-stone-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {service.features.length > 4 && (
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : service.id)}
                      className="text-amber-600 text-sm font-semibold flex items-center gap-1.5 hover:text-amber-500 transition-colors cursor-pointer mb-6"
                    >
                      {isExpanded ? 'Show less' : `View all ${service.features.length} features`}
                      <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`}></i>
                    </button>
                  )}

                  <a
                    href="#consultation"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white text-sm font-semibold rounded-full hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Enquire Now
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
